import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface FAQ {
  question: string;
  answer: string;
}

const faqs: FAQ[] = [
  {
    question: "Ce face această carte diferită de alte cărți despre depresie?",
    answer: "Spre deosebire de alte cărți care oferă doar teorie, 'Depresia, Superputerea Ta!' este un sistem complet de transformare cu: 1) 27 de exerciții practice testate cu peste 10,000 de cititori, 2) 5 tehnici de urgență pentru atacuri de panică, și 3) jurnal structurat pentru monitorizarea progresului. Este scrisă din experiență personală reală, nu din teorie."
  },
  {
    question: "Cât de repede voi vedea rezultate?",
    answer: "98% dintre cititorii noștri raportează îmbunătățiri semnificative în primele 30 de zile de aplicare a metodelor din carte. Sistemul este proiectat să funcționeze în pași mici, dedicând doar 15-30 minute pe zi. Vei primi un plan clar, pas cu pas, care te ghidează de la 'Nu mai pot' la 'Sunt mai puternic ca niciodată'."
  },
  {
    question: "Cum știu că va funcționa pentru mine?",
    answer: "Oferim garanție de returnare 30 de zile, fără întrebări. Dacă nu vezi îmbunătățiri în starea ta după aplicarea metodelor, îți returnăm toți banii. Peste 10,000 de cititori au folosit deja aceste tehnici cu succes, raportând îmbunătățiri semnificative în starea lor emoțională și mentală."
  },
  {
    question: "Cine este autorul și de ce ar trebui să am încredere?",
    answer: "Sunt Andrei Ungureanu (@omulcutourette), și am trecut personal prin depresie severă. Nu sunt doar un alt 'expert' care teoretizează - am dezvoltat aceste tehnici prin încercări și greșeli, transformându-mi propria suferință în putere. Acum, cu peste 1.2 milioane de urmăritori și ca câștigător iUmor, folosesc platforma mea pentru a ajuta și alții să facă același lucru."
  },
  {
    question: "Pot folosi cartea împreună cu terapia/medicația?",
    answer: "Absolut! Cartea este concepută ca un instrument complementar, nu ca înlocuitor pentru tratamentul medical. Mulți dintre cititorii noștri folosesc tehnicile din carte împreună cu terapia și/sau medicația, raportând rezultate mai bune în recuperare. Metodele sunt compatibile și pot amplifica efectele tratamentului tradițional."
  },
  {
    question: "Care sunt detaliile practice (livrare, plată, etc.)?",
    answer: "Livrare rapidă în 24-48h prin curier rapid, cu tracking în timp real. Acceptăm toate metodele de plată (card, ramburs, Apple/Google Pay), 100% securizat. Preț special de lansare 60 RON (în loc de 89.99 RON). Ofertă disponibilă pentru o perioadă limitată!"
  },
  {
    question: "Ce fac dacă mă simt copleșit să încep?",
    answer: "Te înțelegem perfect. De aceea am structurat cartea în pași mici și ușor de urmărit. Începi cu exerciții simple de 5-10 minute și progresezi în ritmul tău. Cartea te ghidează pas cu pas prin proces, cu explicații clare și exemple concrete. Nu ești singur în această călătorie - fiecare exercițiu este explicat în detaliu, cu sfaturi practice pentru momentele dificile."
  }
];

export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleClick = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 sm:py-24 bg-black/50 px-4 sm:px-6">
      <div className="container mx-auto max-w-4xl">
        <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12 bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent">
          Întrebări Frecvente
        </h2>
        
        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            const buttonId = `faq-button-${index}`;
            const contentId = `faq-content-${index}`;

            return (
              <div 
                key={index}
                className="bg-gradient-to-br from-black/40 to-black/60 rounded-xl border border-red-500/20 overflow-hidden"
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
              >
                <button
                  id={buttonId}
                  className="w-full px-6 py-4 text-left flex items-center justify-between gap-4"
                  onClick={() => handleClick(index)}
                  aria-controls={contentId}
                  {...(isOpen ? { 'aria-expanded': 'true' } : { 'aria-expanded': 'false' })}
                >
                  <span 
                    className="font-semibold text-base sm:text-lg"
                    itemProp="name"
                  >
                    {faq.question}
                  </span>
                  {isOpen ? (
                    <ChevronUp className="w-5 h-5 flex-shrink-0 text-red-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 flex-shrink-0 text-red-500" />
                  )}
                </button>
                
                {isOpen && (
                  <div 
                    id={contentId}
                    className="px-6 pb-4 text-gray-300"
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                    role="region"
                    aria-labelledby={buttonId}
                  >
                    <p 
                      className="text-sm sm:text-base"
                      itemProp="text"
                    >
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
